import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { getCurrentUser } from "../services/authService";
import { can } from "../services/permissions";
import { permissionFor } from "../services/routePermissions";

/**
 * A route the caller is not entitled to should not render at all.
 *
 * Hiding the sidebar entry was the only guard the portal had, so typing the
 * address still opened the screen — it then filled with 403s, which reads to
 * whoever is looking at it like the portal is broken rather than like an
 * answer. This turns that into a plain refusal, on the one permission the
 * route map names for the path.
 *
 * It is a courtesy, not a control: the API refuses every request again
 * regardless, which is why a permission granted mid-session is enough on its
 * own — `/business/me` is refetched when the tab comes back to the front, so
 * the guard opens without a re-login. The reverse holds too: a permission
 * withdrawn closes it the same way.
 *
 * Since file 03 a write implies its module's read, and `/me` reports the
 * implied name, so somebody holding only `products.create` passes a
 * `products.view` guard here exactly as they pass it on the server.
 */
export default function RequirePermission({ path, children }) {
  const { t } = useTranslation();
  const permission = permissionFor(path);

  if (!permission || can(getCurrentUser(), permission)) {
    return children;
  }

  return (
    <div className="card mx-auto max-w-lg px-6 py-12 text-center">
      <h1 className="text-lg font-semibold">{t("permissions.deniedTitle")}</h1>
      <p className="mt-2 text-sm text-ink-2">{t("permissions.deniedBody")}</p>
      <Link to="/" className="btn-primary mt-5 inline-block">
        {t("permissions.deniedHome")}
      </Link>
    </div>
  );
}
